import React from "react";
import { map, slice, trim, compact, times } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";

import {
  getSortedChats,
  getComposingHomie,
  getCounterForChat,
} from "selectors/chat/chat";
import ChatImage from "../shared/ChatImage";
import Notification from "../shared/Notification";
import Composing from "../shared/Composing";

function chatInitials(chatName: string) {
  if (chatName.length < 2) {
    return chatName.toUpperCase();
  }

  const parts = compact(chatName.split(/[-_/\\.@\s]/));
  const initials =
    parts.length >= 2
      ? trim(parts[0])[0] + trim(parts[1])[0]
      : chatName[0] + chatName[1];

  return initials.toUpperCase();
}

type Chat = {
  id: string;
  name: string;
  state?: { last_read_id: string | null; last_message_id: string | null };
};

function Chat({
  id,
  name,
  selected,
  state,
}: Chat & {
  selected: boolean;
}) {
  const dispatch = useDispatch();
  const counter = useSelector((state) =>
    getCounterForChat(state, { chatId: id }),
  );
  const composingHomie = useSelector((state) =>
    getComposingHomie(state, { chatId: id }),
  );

  function select() {
    dispatch({
      type: "chat/NAVIGATE",
      payload: { route: "showChat", params: { chatId: id } },
    });
  }

  return (
    <button
      className={classnames("chat__quick_select_item", {
        "chat__quick_select_item--active": selected,
      })}
      onClick={select}
      title={name}
    >
      <div className="grid stack-container">
        <ChatImage chatId={id} name={name} />
        <Notification
          counter={state?.last_read_id ? counter : -1}
          className="stack-item w-fit h-fit z-50 justify-self-end"
        />
        {composingHomie && (
          <Composing className="stack-item z-50 justify-self-end self-end" />
        )}
      </div>
      <span className="chatName font-semibold text-xs">
        {chatInitials(name)}
      </span>
    </button>
  );
}

export default function QuickChatSelect({ chatId }: { chatId: string }) {
  const chats: Chat[] = useSelector((state) =>
    slice(getSortedChats(state), 0, 10),
  );

  return (
    <div className="chat__quick_select">
      {map(
        chats,
        (chat) =>
          chat.id && (
            <Chat key={chat.id} selected={chat.id === chatId} {...chat} />
          ),
      )}
      {times(10 - chats.length, (i) => (
        <div key={i} className={"placeholder"} />
      ))}
    </div>
  );
}
